/**
 * PaywallModal.tsx — Amargi
 * Overlay de blocage freemium avec 3 niveaux :
 *  1. Compteur 12h depuis la dernière évaluation
 *  2. Connexion (Google / Magic Link) → 7 jours accès complet
 *  3. Offre Pro mensuelle / annuelle
 */

import React, { useEffect, useState } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { Clock, Check, Zap, Star, ArrowLeft } from 'lucide-react';
import AppLink from './AppLink';
import AuthModal from './AuthModal';

// ─── Props ────────────────────────────────────────────────────────────────────

interface PaywallModalProps {
  nextAvailableAt: Date;
  countToday: number;
  dailyLimit: number;
  onBack?: () => void;
}

// ─── Countdown hook ───────────────────────────────────────────────────────────

function useCountdown(target: Date) {
  const calc = () => Math.max(0, target.getTime() - Date.now());
  const [ms, setMs] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setMs(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);

  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { h: pad(h), m: pad(m), s: pad(s), done: ms === 0 };
}

// ─── Composant ────────────────────────────────────────────────────────────────

export default function PaywallModal({ nextAvailableAt, countToday, dailyLimit, onBack }: PaywallModalProps) {
  const { h, m: min, s, done } = useCountdown(nextAvailableAt);
  const [showAuth, setShowAuth] = useState(false);

  return (
    <>
      <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
        {/* Fond flouté */}
        <div className="absolute inset-0 bg-ink/25 backdrop-blur-sm" />

        <m.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-paper rounded-[28px] shadow-2xl flex flex-col max-h-[90dvh]"
        >
          {/* ── En-tête ── */}
          <div className="px-6 pt-5 pb-4 border-b border-sand/40 flex-shrink-0">
            {/* Bouton retour */}
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-[12px] text-taupe hover:text-ink transition-colors mb-3 -ml-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Retour à l’accueil</span>
              </button>
            )}
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-amber/15 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-[11px] uppercase tracking-widest font-bold text-amber-600">
                Accès gratuit
              </span>
            </div>
            <h2 className="font-serif font-light text-[22px] text-ink leading-tight">
              {countToday >= dailyLimit
                ? 'Vous avez fait vos 2 bilans.'
                : `Bilan ${countToday} sur ${dailyLimit} utilisé.`}
            </h2>
            <p className="text-[13px] text-taupe mt-1 leading-relaxed">
              L'accès gratuit inclut 2 bilans par tranche de 12h.
            </p>
          </div>

          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 overscroll-contain">

            {/* ── Bloc 1 : Compteur 12h ── */}
            <div className="bg-white rounded-[16px] border border-sand/60 px-5 py-4">
              <p className="text-[10px] uppercase tracking-widest text-taupe font-semibold mb-1.5">
                Prochain bilan dans
              </p>
              {done ? (
                <p className="text-[20px] font-serif font-bold text-lapis">Disponible maintenant</p>
              ) : (
                <p className="font-mono text-[32px] font-bold text-ink leading-none tabular-nums">
                  {h}:{min}:{s}
                </p>
              )}
              <p className="text-[11px] text-taupe/70 mt-1.5">
                12h depuis votre dernière évaluation
              </p>
            </div>

            {/* ── Séparateur ── */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-sand/50" />
              <span className="text-[11px] uppercase tracking-widest text-taupe/60 font-medium">ou</span>
              <div className="flex-1 h-px bg-sand/50" />
            </div>

            {/* ── Bloc 2 : Connexion → 7 jours accès complet ── */}
            <div className="bg-lapis/6 rounded-[16px] border border-lapis/20 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-lapis flex-shrink-0" />
                <p className="font-semibold text-ink text-[14px]">
                  Essai gratuit 7 jours — accès complet
                </p>
              </div>
              <ul className="space-y-1.5 mb-4">
                {[
                  'Évaluations illimitées chaque jour',
                  'Accès multi-interface (mobile, tablette, desktop)',
                  'Historique complet de vos 30 derniers bilans',
                  'Exercices variés selon votre profil du moment',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[12px] text-taupe">
                    <Check className="w-3.5 h-3.5 text-lapis flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setShowAuth(true)}
                className="w-full py-2.5 rounded-[10px] bg-lapis text-white font-semibold text-[13px] hover:bg-lapis/90 transition-colors"
              >
                Activer 7 jours gratuits
              </button>
              <p className="text-[10px] text-taupe/50 text-center mt-2">
                Sans carte bancaire · Google ou email
              </p>
            </div>

            {/* ── Bloc 3 : Pro ── */}
            <div className="bg-amber/6 rounded-[16px] border border-amber/25 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="font-semibold text-ink text-[14px]">Amargi Pro — accès illimité</p>
              </div>
              <div className="flex gap-3 mb-3">
                <div className="flex-1 bg-white rounded-[10px] border border-sand/60 p-2.5 text-center">
                  <p className="text-[20px] font-serif font-bold text-ink">12,99 €</p>
                  <p className="text-[11px] text-taupe">par mois</p>
                </div>
                <div className="flex-1 bg-white rounded-[10px] border border-amber/40 p-2.5 text-center relative">
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">
                    Économisez 62%
                  </span>
                  <p className="text-[20px] font-serif font-bold text-ink">59 €</p>
                  <p className="text-[11px] text-taupe">par an</p>
                </div>
              </div>
              <AppLink
                to="/fr/tarifs"
                className="block w-full py-2.5 rounded-[10px] bg-amber-500 text-white font-semibold text-[13px] text-center hover:bg-amber-600 transition-colors"
              >
                Voir les offres Pro
              </AppLink>
            </div>

          </div>

          {/* Note de bas de page */}
          <div className="px-6 pb-5 flex-shrink-0 border-t border-sand/30 pt-3">
            <p className="text-[11px] text-taupe/60 text-center leading-relaxed">
              La limite de 2 bilans / 12h respecte votre système nerveux.
              Un bilan le matin et un le soir suffisent pour observer une évolution réelle.
            </p>
          </div>
        </m.div>
      </div>

      {/* AuthModal — s'ouvre par-dessus le PaywallModal (z-[300]) */}
      <AnimatePresence>
        {showAuth && (
          <AuthModal
            onClose={() => setShowAuth(false)}
            title="Activez votre essai gratuit"
            subtitle="Connectez-vous pour débloquer 7 jours d'accès complet — sans carte bancaire."
          />
        )}
      </AnimatePresence>
    </>
  );
}
